import { useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef } from 'react';
import { Dialog } from '../dialog.js';
import { HouseholdSwitcher } from '../household-switcher.js';
import { SyncStatus } from '../sync-status.js';
import {
  AccountNav,
  CollectionsNav,
  ImportNavItem,
  RecentNavItem,
  TagsNav,
} from './nav-sections.js';

/**
 * Everything the phone's tab bar does not have room for.
 *
 * It is a bottom sheet rather than a side drawer because it is opened from a bottom tab:
 * the panel arrives where the thumb already is, and the list of collections starts within
 * reach instead of at the top of the screen.
 *
 * Opening the add-collection dialog closes this first — see `AppShell` — so two overlays
 * are never stacked. `Dialog` saves and restores `body.overflow` on its own, and nesting
 * two of them can restore the wrong value when they unmount out of order.
 */
export function NavSheet({
  onClose,
  onAddCollection,
}: {
  onClose: () => void;
  onAddCollection: () => void;
}) {
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const href = useRouterState({ select: (state) => state.location.href });
  const openedAt = useRef(href);

  // Focus has to come into the panel, or it stays on the More button now behind the scrim.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  // A backstop for route changes this sheet did not start, such as the browser back button.
  // Links close it directly through `onNavigate`, which also covers a link to the open page.
  useEffect(() => {
    if (href !== openedAt.current) onClose();
  }, [href, onClose]);

  return (
    <Dialog onClose={onClose} className="nav-sheet" aria-labelledby={headingId}>
      <div className="nav-sheet-head">
        <h2 className="brand nav-sheet-title" id={headingId} ref={heading} tabIndex={-1}>
          <img className="brand-mark" src="/logo.png" alt="" width={32} height={32} /> Platewell
        </h2>
        <span className="spacer" />
        <SyncStatus />
      </div>

      <HouseholdSwitcher />

      <nav className="nav" aria-label="More">
        <ImportNavItem onNavigate={onClose} />
        <RecentNavItem onNavigate={onClose} />
        <CollectionsNav onAdd={onAddCollection} onNavigate={onClose} />
        <TagsNav onNavigate={onClose} />
      </nav>

      <div className="nav-sheet-account">
        <AccountNav onNavigate={onClose} />
      </div>
    </Dialog>
  );
}
